import { useState, useEffect, useCallback, Fragment } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Package, Search, RefreshCw, AlertTriangle, 
  XCircle, Filter, IndianRupee, MapPin, Warehouse, Store, Globe, CheckCircle,
  Download, ChevronRight, ChevronDown, Tag
} from 'lucide-react';
import { EXPIRY_FILTERS, expiryParams, exportRowsToCsv } from '@/utils/stockExport';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import PricingSticker from '@/components/PricingSticker';

const StoreStock = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('my_stock');
  const [stock, setStock] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [summary, setSummary] = useState({});
  const [allProductsSummary, setAllProductsSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingAllProducts, setLoadingAllProducts] = useState(false);
  const [search, setSearch] = useState('');          // what the user typed
  const [searchInput, setSearchInput] = useState(''); // debounced into `search` so each keystroke / scan fires one request
  useEffect(() => { const t = setTimeout(() => setSearch(searchInput.trim()), 300); return () => clearTimeout(t); }, [searchInput]);
  const [filterType, setFilterType] = useState('all');
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [groupBy, setGroupBy] = useState('product');
  const [batchSearch, setBatchSearch] = useState('');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [includeZero, setIncludeZero] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [expandedBatches, setExpandedBatches] = useState([]);
  
  // Track Stock dialog state
  const [trackDialogOpen, setTrackDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockLocations, setStockLocations] = useState({ warehouses: [], stores: [] });
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Price-label sticker dialog state (Store Manager only)
  const [stickerOpen, setStickerOpen] = useState(false);
  const [stickerProduct, setStickerProduct] = useState(null);

  // Store Manager vs Cashier gate. Mirrors the heuristic used elsewhere
  // (POSBilling.js): a cashier is scoped to a tiny set of permissions, a
  // manager carries 3+ or the broader 'sales/dashboard/*' entries.
  const isStoreManager = user?.permissions?.length > 2 ||
    user?.permissions?.some(p => ['sales', 'dashboard', '*'].includes(typeof p === 'string' ? p : p?.module));

  const storeId = user?.entity_id;

  const fetchStock = useCallback(async () => {
    if (!storeId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({ store_id: storeId, group_by: groupBy });
      
      if (search) params.append('search', search);
      if (filterType && filterType !== 'all') params.append('filter_type', filterType);
      if (selectedCategory && selectedCategory !== 'all') params.append('category_id', selectedCategory);
      if (selectedBrand && selectedBrand !== 'all') params.append('brand_id', selectedBrand);
      if (groupBy === 'batch') {
        if (batchSearch) params.append('batch_search', batchSearch);
        if (includeZero) params.append('include_zero', 'true');
        const ep = expiryParams(expiryFilter);
        Object.entries(ep).forEach(([k, v]) => params.append(k, String(v)));
      }
      
      const response = await api.get(`/store-inventory/stock?${params}`);
      setStock(response.data.items || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error fetching stock:', error);
    } finally {
      setLoading(false);
    }
  }, [storeId, search, filterType, selectedCategory, selectedBrand, groupBy, batchSearch, expiryFilter, includeZero]);

  const toggleProductBatches = async (item) => {
    if (expandedProduct === item.product_id) {
      setExpandedProduct(null);
      setExpandedBatches([]);
      return;
    }
    setExpandedProduct(item.product_id);
    setExpandedBatches([]);
    try {
      const res = await api.get(`/inventory/batches/${item.product_id}`, { params: { store_id: storeId } });
      setExpandedBatches(res.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch batches', variant: 'destructive' });
    }
  };

  const handleExportStock = () => {
    if (!stock.length) {
      toast({ title: 'Nothing to export', description: 'No stock rows in the current view' });
      return;
    }
    const columns = groupBy === 'batch'
      ? [
          { label: 'Product', key: 'product_name' },
          { label: 'SKU', key: 'sku' },
          { label: 'Batch', key: 'batch_number' },
          { label: 'Expiry', key: 'expiry_date' },
          { label: 'Qty', key: 'quantity' },
          { label: 'Selling Price', key: 'selling_price' },
          { label: 'Stock Value', key: 'stock_value' }
        ]
      : [
          { label: 'Product', key: 'product_name' },
          { label: 'SKU', key: 'sku' },
          { label: 'Category', key: 'category_name' },
          { label: 'Brand', key: 'brand_name' },
          { label: 'Total Qty', key: 'quantity' },
          { label: 'Batches', key: 'batch_count' },
          { label: 'Nearest Expiry', key: 'nearest_expiry' },
          { label: 'Stock Value', key: 'stock_value' }
        ];
    exportRowsToCsv(stock, columns, `store-stock-${groupBy}-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const fetchFilters = useCallback(async () => {
    try {
      const [catRes, brandRes] = await Promise.all([
        api.get('/categories?limit=500'),
        api.get('/brands?limit=500')
      ]);
      setCategories(catRes.data || []);
      setBrands(brandRes.data || []);
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  }, []);

  // Fetch ALL products from HQ catalog (for "All Products" tab)
  const fetchAllProducts = useCallback(async () => {
    if (!storeId) return;
    
    setLoadingAllProducts(true);
    try {
      const params = {
        store_id: storeId,
        limit: 500
      };
      if (search) params.search = search;
      if (selectedCategory !== 'all') params.category_id = selectedCategory;
      if (selectedBrand !== 'all') params.brand_id = selectedBrand;
      if (filterType === 'in_stock') params.stock_filter = 'in_stock';
      if (filterType === 'out_of_stock') params.stock_filter = 'out_of_stock';
      
      const res = await api.get('/store-inventory/all-products', { params });
      setAllProducts(res.data?.items || []);
      setAllProductsSummary(res.data?.summary || {});
    } catch (error) {
      console.error('Error fetching all products:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to fetch products catalog', 
        variant: 'destructive' 
      });
    } finally {
      setLoadingAllProducts(false);
    }
  }, [storeId, search, selectedCategory, selectedBrand, filterType, toast]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    if (activeTab === 'my_stock') {
      fetchStock();
    } else {
      fetchAllProducts();
    }
  }, [activeTab, fetchStock, fetchAllProducts]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'out_of_stock':
        return <Badge variant="destructive" className="gap-1"><XCircle className="w-3 h-3" /> Out of Stock</Badge>;
      case 'low_stock':
        return <Badge variant="warning" className="bg-amber-100 text-amber-700 gap-1"><AlertTriangle className="w-3 h-3" /> Low Stock</Badge>;
      default:
        return <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">In Stock</Badge>;
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (activeTab === 'my_stock') {
      fetchStock();
    } else {
      fetchAllProducts();
    }
  };

  const clearFilters = () => {
    setSearch(''); setSearchInput('');
    setFilterType('all');
    setSelectedCategory('all');
    setSelectedBrand('all');
  };

  // Track stock across all locations
  const trackStock = async (product) => {
    setSelectedProduct(product);
    setTrackDialogOpen(true);
    setLoadingLocations(true);
    
    try {
      const res = await api.get(`/store-inventory/product/${product.product_id}/network-stock`);
      setStockLocations({
        warehouses: res.data?.warehouses?.map(w => ({
          location_id: w.warehouse_id,
          location_name: w.warehouse_name,
          location_code: w.warehouse_code,
          quantity: w.quantity
        })) || [],
        stores: res.data?.stores?.map(s => ({
          location_id: s.store_id,
          location_name: s.store_name,
          location_code: s.store_code,
          quantity: s.quantity,
          store_type: s.store_type
        })) || []
      });
    } catch (error) {
      console.error('Error fetching stock locations:', error);
      // Fallback to old API
      try {
        const res = await api.get(`/inventory/product/${product.product_id}/stock-locations`);
        setStockLocations(res.data || { warehouses: [], stores: [] });
      } catch (e) {
        toast({ 
          title: 'Error', 
          description: 'Failed to fetch stock locations', 
          variant: 'destructive' 
        });
        setStockLocations({ warehouses: [], stores: [] });
      }
    } finally {
      setLoadingLocations(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="store-stock-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Store Stock</h1>
          <p className="text-gray-500">View and manage store inventory</p>
        </div>
        <Button onClick={activeTab === 'my_stock' ? fetchStock : fetchAllProducts} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="my_stock" className="gap-2">
            <Package className="w-4 h-4" />
            My Stock
          </TabsTrigger>
          <TabsTrigger value="all_products" className="gap-2">
            <Globe className="w-4 h-4" />
            All Products
          </TabsTrigger>
        </TabsList>

        {/* My Stock Tab */}
        <TabsContent value="my_stock" className="space-y-6 mt-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Products</p>
                <p className="text-2xl font-bold text-blue-800">{summary.total_products || 0}</p>
              </div>
              <Package className="w-10 h-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 font-medium">Stock Value</p>
                <p className="text-2xl font-bold text-emerald-800 flex items-center">
                  <IndianRupee className="w-5 h-5" />
                  {(summary.total_stock_value || 0).toLocaleString()}
                </p>
              </div>
              <IndianRupee className="w-10 h-10 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 font-medium">Low Stock</p>
                <p className="text-2xl font-bold text-amber-800">{summary.low_stock_count || 0}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Out of Stock</p>
                <p className="text-2xl font-bold text-red-800">{summary.out_of_stock_count || 0}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm text-gray-600 mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name, SKU, barcode..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setSearch(searchInput.trim()); }}
                  className="pl-10"
                  data-testid="stock-search-input"
                />
              </div>
            </div>

            <div className="w-[180px]">
              <label className="text-sm text-gray-600 mb-1 block">Status</label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="low_stock">Low Stock</SelectItem>
                  <SelectItem value="out_of_stock">Out of Stock</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-[180px]">
              <label className="text-sm text-gray-600 mb-1 block">Category</label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-[180px]">
              <label className="text-sm text-gray-600 mb-1 block">Brand</label>
              <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                <SelectTrigger>
                  <SelectValue placeholder="All Brands" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  {brands.map((brand) => (
                    <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>

            <Button type="button" variant="outline" onClick={clearFilters}>
              Clear
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Product-wise / Batch-wise switch */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden bg-white" data-testid="store-stock-groupby-switch">
          <button
            type="button"
            onClick={() => setGroupBy('product')}
            className={`px-4 py-2 text-sm transition-colors ${groupBy === 'product' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            data-testid="store-stock-groupby-product-btn"
          >
            Product-wise
          </button>
          <button
            type="button"
            onClick={() => setGroupBy('batch')}
            className={`px-4 py-2 text-sm transition-colors ${groupBy === 'batch' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            data-testid="store-stock-groupby-batch-btn"
          >
            Batch-wise
          </button>
        </div>

        {groupBy === 'batch' && (
          <div className="flex flex-wrap items-center gap-3" data-testid="store-stock-batch-filters">
            <Input
              placeholder="Batch / lot number..."
              value={batchSearch}
              onChange={(e) => setBatchSearch(e.target.value)}
              className="w-56"
              data-testid="store-stock-batch-search-input"
            />
            <Select value={expiryFilter} onValueChange={setExpiryFilter}>
              <SelectTrigger className="w-48" data-testid="store-stock-expiry-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPIRY_FILTERS.map(f => (
                  <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={includeZero}
                onChange={(e) => setIncludeZero(e.target.checked)}
                data-testid="store-stock-include-zero-toggle"
              />
              Show zero-qty batches
            </label>
          </div>
        )}

        <div className="lg:ml-auto">
          <Button variant="outline" size="sm" onClick={handleExportStock} data-testid="store-stock-export-btn">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
        </div>
      </div>

      {/* Stock Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Product</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Barcode</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>{groupBy === 'batch' ? 'Batch / Expiry' : 'Batches'}</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Selling Price</TableHead>
                  <TableHead className="text-right">Stock Value</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-10 text-gray-500">
                      Loading stock data...
                    </TableCell>
                  </TableRow>
                ) : stock.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-10 text-gray-500">
                      <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      No stock found
                    </TableCell>
                  </TableRow>
                ) : (
                  stock.map((item, index) => (
                    <Fragment key={item.id || `${item.product_id}-${index}`}>
                    <TableRow className="hover:bg-gray-50">
                      <TableCell>
                        <p className="font-medium text-gray-800">{item.product_name}</p>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{item.sku}</TableCell>
                      <TableCell className="font-mono text-sm text-gray-600">
                        {item.barcode || '-'}
                      </TableCell>
                      <TableCell className="text-gray-600">{item.category_name || '-'}</TableCell>
                      <TableCell className="text-gray-600">{item.brand_name || '-'}</TableCell>
                      <TableCell className="text-gray-600 text-sm">
                        {groupBy === 'batch' ? (
                          <>
                            {item.batch_number || 'Default'}
                            {item.expiry_date && (
                              <p className="text-xs text-gray-400">Exp: {String(item.expiry_date).slice(0, 10)}</p>
                            )}
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => toggleProductBatches(item)}
                            className="flex items-center gap-1 text-emerald-700 hover:underline"
                            data-testid={`store-stock-expand-batches-${index}`}
                          >
                            {expandedProduct === item.product_id ? (
                              <ChevronDown className="w-3 h-3" />
                            ) : (
                              <ChevronRight className="w-3 h-3" />
                            )}
                            {item.batch_count || 0} batch{(item.batch_count || 0) === 1 ? '' : 'es'}
                            {item.nearest_expiry && (
                              <span className="text-xs text-gray-400 ml-1">(exp {String(item.nearest_expiry).slice(0, 10)})</span>
                            )}
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {item.quantity} {item.uom}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="flex items-center justify-end">
                          <IndianRupee className="w-3 h-3" />
                          {item.selling_price?.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-emerald-600">
                        <span className="flex items-center justify-end">
                          <IndianRupee className="w-3 h-3" />
                          {item.stock_value?.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell>{getStatusBadge(item.status)}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => trackStock(item)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            data-testid={`track-stock-${item.product_id}`}
                          >
                            <MapPin className="w-3 h-3 mr-1" />
                            Track Stock
                          </Button>
                          {isStoreManager && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setStickerProduct({ id: item.product_id, selling_price: item.selling_price });
                                setStickerOpen(true);
                              }}
                              className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                              data-testid={`print-label-${item.product_id}`}
                            >
                              <Tag className="w-3 h-3 mr-1" />
                              Print Label
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {groupBy === 'product' && expandedProduct === item.product_id && (
                      expandedBatches.length === 0 ? (
                        <TableRow className="bg-gray-50" data-testid={`store-stock-batch-subrow-${index}`}>
                          <TableCell colSpan={11} className="text-sm text-gray-500 pl-8">No batches with stock</TableCell>
                        </TableRow>
                      ) : (
                        expandedBatches.map((b, bi) => (
                          <TableRow key={bi} className="bg-gray-50" data-testid={bi === 0 ? `store-stock-batch-subrow-${index}` : undefined}>
                            <TableCell className="pl-8 text-xs text-gray-500">Batch</TableCell>
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell className="text-sm font-medium text-gray-800">
                              {b.batch_number}
                              {b.expiry_date && (
                                <p className="text-xs text-gray-400">Exp: {String(b.expiry_date).slice(0, 10)}</p>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-sm font-semibold">{b.quantity}</TableCell>
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell />
                          </TableRow>
                        ))
                      )
                    )}
                    </Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

          {/* Total Count */}
          {!loading && stock.length > 0 && (
            <p className="text-sm text-gray-500 text-center">
              Showing {stock.length} of {summary.total_products || 0} products
            </p>
          )}
        </TabsContent>

        {/* All Products Tab */}
        <TabsContent value="all_products" className="space-y-6 mt-6">
          {/* All Products Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-indigo-600 font-medium">HQ Catalog Products</p>
                    <p className="text-2xl font-bold text-indigo-800">{allProductsSummary.total_products || 0}</p>
                  </div>
                  <Globe className="w-10 h-10 text-indigo-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-600 font-medium">In My Stock</p>
                    <p className="text-2xl font-bold text-emerald-800">{allProductsSummary.in_stock_count || 0}</p>
                  </div>
                  <CheckCircle className="w-10 h-10 text-emerald-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600 font-medium">Not in Stock</p>
                    <p className="text-2xl font-bold text-red-800">{allProductsSummary.out_of_stock_count || 0}</p>
                  </div>
                  <XCircle className="w-10 h-10 text-red-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">My Stock Value</p>
                    <p className="text-2xl font-bold text-purple-800 flex items-center">
                      <IndianRupee className="w-5 h-5" />
                      {(allProductsSummary.total_stock_value || 0).toLocaleString()}
                    </p>
                  </div>
                  <IndianRupee className="w-10 h-10 text-purple-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* All Products Filters */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm text-gray-600 mb-1 block">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by name, SKU, barcode..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') setSearch(searchInput.trim()); }}
                      className="pl-10"
                      data-testid="all-products-search-input"
                    />
                  </div>
                </div>

                <div className="w-[180px]">
                  <label className="text-sm text-gray-600 mb-1 block">Stock Status</label>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger>
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Products</SelectItem>
                      <SelectItem value="in_stock">In My Stock</SelectItem>
                      <SelectItem value="out_of_stock">Not in Stock</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="w-[180px]">
                  <label className="text-sm text-gray-600 mb-1 block">Category</label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="w-[180px]">
                  <label className="text-sm text-gray-600 mb-1 block">Brand</label>
                  <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Brands" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Brands</SelectItem>
                      {brands.map((brand) => (
                        <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>

                <Button type="button" variant="outline" onClick={clearFilters}>
                  Clear
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* All Products Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Product</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Brand</TableHead>
                      <TableHead className="text-right">My Stock</TableHead>
                      <TableHead className="text-right">Network Stock</TableHead>
                      <TableHead className="text-right">Selling Price</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Track</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingAllProducts ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-10 text-gray-500">
                          Loading products catalog...
                        </TableCell>
                      </TableRow>
                    ) : allProducts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-10 text-gray-500">
                          <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                          No products found
                        </TableCell>
                      </TableRow>
                    ) : (
                      allProducts.map((item) => (
                        <TableRow key={item.product_id} className={`hover:bg-gray-50 ${item.my_stock === 0 ? 'bg-red-50/30' : ''}`}>
                          <TableCell>
                            <p className="font-medium text-gray-800">{item.product_name}</p>
                            {item.barcode && <p className="text-xs text-gray-500 font-mono">{item.barcode}</p>}
                          </TableCell>
                          <TableCell className="font-mono text-sm">{item.sku}</TableCell>
                          <TableCell className="text-gray-600">{item.category_name || '-'}</TableCell>
                          <TableCell className="text-gray-600">{item.brand_name || '-'}</TableCell>
                          <TableCell className="text-right font-semibold">
                            <span className={item.my_stock > 0 ? 'text-emerald-600' : 'text-red-500'}>
                              {item.my_stock} {item.uom}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <span className="font-semibold text-indigo-600">{item.network_stock?.total || 0}</span>
                              {item.network_stock?.available_at_other_stores > 0 && (
                                <Badge variant="outline" className="text-xs text-indigo-600 border-indigo-300">
                                  {item.network_stock.available_at_other_stores} stores
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="flex items-center justify-end">
                              <IndianRupee className="w-3 h-3" />
                              {item.selling_price?.toLocaleString()}
                            </span>
                          </TableCell>
                          <TableCell>{getStatusBadge(item.status)}</TableCell>
                          <TableCell className="text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => trackStock(item)}
                              className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                            >
                              <MapPin className="w-3 h-3 mr-1" />
                              Track
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Total Count */}
          {!loadingAllProducts && allProducts.length > 0 && (
            <p className="text-sm text-gray-500 text-center">
              Showing {allProducts.length} of {allProductsSummary.total_products || 0} products from HQ catalog
            </p>
          )}
        </TabsContent>
      </Tabs>

      {/* Track Stock Dialog */}
      <Dialog open={trackDialogOpen} onOpenChange={setTrackDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Stock Availability
            </DialogTitle>
          </DialogHeader>
          
          {selectedProduct && (
            <div className="space-y-4">
              {/* Product Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800">{selectedProduct.product_name}</h3>
                <div className="flex gap-4 mt-2 text-sm text-gray-600">
                  <span>SKU: <span className="font-mono">{selectedProduct.sku}</span></span>
                  {selectedProduct.barcode && (
                    <span>Barcode: <span className="font-mono">{selectedProduct.barcode}</span></span>
                  )}
                </div>
              </div>

              {loadingLocations ? (
                <div className="text-center py-8 text-gray-500">
                  Loading stock locations...
                </div>
              ) : (
                <div className="space-y-4 max-h-[400px] overflow-y-auto">
                  {/* Warehouses */}
                  <div>
                    <h4 className="font-medium text-gray-700 flex items-center gap-2 mb-3">
                      <Warehouse className="w-4 h-4 text-purple-600" />
                      Warehouses
                    </h4>
                    {stockLocations.warehouses?.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No stock in any warehouse</p>
                    ) : (
                      <div className="space-y-2">
                        {stockLocations.warehouses?.map((wh) => (
                          <div 
                            key={wh.location_id} 
                            className="flex items-center justify-between bg-purple-50 rounded-lg p-3"
                          >
                            <div>
                              <p className="font-medium text-gray-800">{wh.location_name}</p>
                              <p className="text-xs text-gray-500">{wh.location_code}</p>
                            </div>
                            <div className="text-right">
                              <p className={`text-lg font-bold ${wh.quantity > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                {wh.quantity} {wh.uom || 'PCS'}
                              </p>
                              {wh.quantity === 0 && (
                                <Badge variant="destructive" className="text-xs">Out of Stock</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Stores */}
                  <div>
                    <h4 className="font-medium text-gray-700 flex items-center gap-2 mb-3">
                      <Store className="w-4 h-4 text-blue-600" />
                      Stores
                    </h4>
                    {stockLocations.stores?.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No stock in any store</p>
                    ) : (
                      <div className="space-y-2">
                        {stockLocations.stores?.map((st) => (
                          <div 
                            key={st.location_id} 
                            className={`flex items-center justify-between rounded-lg p-3 ${
                              st.location_id === storeId ? 'bg-emerald-50 border border-emerald-200' : 'bg-blue-50'
                            }`}
                          >
                            <div>
                              <p className="font-medium text-gray-800">
                                {st.location_name}
                                {st.location_id === storeId && (
                                  <Badge className="ml-2 bg-emerald-500 text-xs">Current Store</Badge>
                                )}
                              </p>
                              <p className="text-xs text-gray-500">{st.location_code}</p>
                            </div>
                            <div className="text-right">
                              <p className={`text-lg font-bold ${st.quantity > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                {st.quantity} {st.uom || 'PCS'}
                              </p>
                              {st.quantity === 0 && (
                                <Badge variant="destructive" className="text-xs">Out of Stock</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Total Summary */}
                  <div className="border-t pt-4 mt-4">
                    <div className="flex justify-between items-center bg-gray-100 rounded-lg p-3">
                      <span className="font-medium text-gray-700">Total Available Stock</span>
                      <span className="text-xl font-bold text-gray-800">
                        {(stockLocations.warehouses?.reduce((sum, w) => sum + (w.quantity || 0), 0) || 0) +
                         (stockLocations.stores?.reduce((sum, s) => sum + (s.quantity || 0), 0) || 0)} PCS
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrackDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price-label sticker printer (Store Manager only) */}
      {isStoreManager && (
        <PricingSticker
          open={stickerOpen}
          onClose={() => { setStickerOpen(false); setStickerProduct(null); }}
          productId={stickerProduct?.id}
          defaultSellingPrice={stickerProduct?.selling_price}
        />
      )}

      <Toaster />
    </div>
  );
};

export default StoreStock;
